import React, { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";
import { pullRefreshBusy } from "../utils/pullRefreshGuard";

const THRESHOLD = 70; // px of pull that counts as "refresh"
const MAX_PULL = 110;
const UPDATE_WAIT_MS = 2000;

/** True when the touch started inside something that scrolls vertically by itself. */
const insideScroller = (node) => {
  for (let el = node; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
    if (el.nodeType !== 1) continue;
    const { overflowY } = window.getComputedStyle(el);
    if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) return true;
  }
  return false;
};

/**
 * Pull down from the top of any page to refresh it — and to pick up a new
 * version of the app. An installed app is resumed, not reopened, so without
 * this a shop only saw an update after closing it completely.
 *
 * It asks the service worker to look for a newer build, then reloads. It stays
 * out of the way of anything that scrolls on its own (sheets, the drawer, a
 * list inside a dialog), of an open overlay (the page behind is locked), and
 * of a gesture that does not start at the very top.
 */
const PullToRefresh = () => {
  const { t } = useI18n();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef(null);
  const distance = useRef(0);

  const refresh = useCallback(async () => {
    if (pullRefreshBusy() && !window.confirm(t("common.pullRefresh.busyConfirm"))) {
      setPull(0);
      return;
    }

    setRefreshing(true);

    try {
      const registration = await navigator.serviceWorker?.getRegistration?.();
      if (registration?.update) {
        await Promise.race([registration.update(), new Promise((resolve) => setTimeout(resolve, UPDATE_WAIT_MS))]);
      }
    } catch {
      // No service worker, or offline: a plain reload is still right
    }

    window.location.reload();
  }, [t]);

  useEffect(() => {
    const onStart = (event) => {
      start.current = null;
      distance.current = 0;

      if (event.touches.length !== 1) return;
      if (window.scrollY > 0 || document.documentElement.scrollTop > 0) return;
      if (document.body.style.overflow === "hidden") return; // an overlay is open
      if (event.target.closest?.('[role="dialog"], input, textarea, select')) return;
      if (insideScroller(event.target)) return;

      start.current = event.touches[0].clientY;
    };

    const onMove = (event) => {
      if (start.current === null) return;

      const delta = event.touches[0].clientY - start.current;

      if (delta <= 0 || window.scrollY > 0) {
        distance.current = 0;
        setPull(0);
        return;
      }

      distance.current = Math.min(delta * 0.5, MAX_PULL);
      setPull(distance.current);
    };

    const onEnd = () => {
      if (start.current === null) return;

      const reached = distance.current >= THRESHOLD;
      start.current = null;
      distance.current = 0;

      if (reached) refresh();
      else setPull(0);
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [refresh]);

  if (pull <= 0 && !refreshing) return null;

  const ready = pull >= THRESHOLD;
  const label = refreshing
    ? t("common.pullRefresh.refreshing")
    : ready
      ? t("common.pullRefresh.release")
      : t("common.pullRefresh.pull");

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] flex justify-center"
      style={{ transform: `translateY(${refreshing ? 24 : Math.max(pull - 24, 0)}px)` }}
    >
      <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-lg ring-1 ring-black/5">
        <span
          aria-hidden="true"
          className={`inline-block text-base ${refreshing ? "animate-spin" : ""}`}
          style={refreshing ? undefined : { transform: `rotate(${ready ? 180 : 0}deg)`, transition: "transform 150ms" }}
        >
          {refreshing ? "⟳" : "↓"}
        </span>
        {label}
      </div>
    </div>
  );
};

export default PullToRefresh;
